import React, { useCallback, useEffect, useRef, useState } from "react";
import { CalendarClock, ChevronLeft, ChevronRight, LayoutGrid, Plus, RefreshCw, Table2 } from "lucide-react";
import useAuth from "../../../hooks/useAuth";
import {
  cancelAppointment,
  completeAppointment,
  createAppointment,
  getAvailableSlots,
  getSchedulingErrorMessage,
  listAppointments,
  markNoShow,
  searchCustomers,
  updateAppointment,
} from "../../../api/schedulingApi";
import {
  Alert,
  Field,
  Modal,
  StatusPill,
  addDays,
  btnDanger,
  btnPrimary,
  btnSecondary,
  card,
  formatDate,
  formatTime,
  inputClass,
  localParts,
  todayIn,
} from "./ui";
import NotificationStatus, { notificationOutcomeText } from "../notifications/NotificationStatus";
import DateRangePicker from "./DateRangePicker";
import useBotNotifications from "../../../hooks/useBotNotifications";
import CustomerAvatar from "./CustomerAvatar";

const ANY_PROVIDER = "any";

function SlotPicker({ botId, timeZone, serviceId, providerId, date, value, onChange }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  useBotNotifications(botId, {
    topic: "appointment",
    onRefresh: () => setRevision((current) => current + 1),
  });

  useEffect(() => {
    if (!date) {
      setSlots([]);
      return;
    }
    let active = true;
    setLoading(true);
    getAvailableSlots(botId, {
      date,
      provider_id: providerId && providerId !== ANY_PROVIDER ? providerId : undefined,
      service_id: serviceId || undefined,
    })
      .then((data) => active && setSlots(data || []))
      .catch(() => active && setSlots([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [botId, serviceId, providerId, date, revision]);

  if (!date) return <p className="text-xs text-slate-400">Choose a date to see free slots.</p>;
  if (loading) return <p className="text-xs text-slate-500">Loading free slots…</p>;
  if (!slots.length) return <p className="text-xs text-amber-700">No free slots on this day. Use a custom time if needed.</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {slots.map((s) => (
        <button
          key={s.start_at}
          type="button"
          onClick={() => onChange(s.start_at)}
          className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
            value === s.start_at ? "border-[#096dea] bg-[#096dea] text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50"
          }`}
        >
          {formatTime(s.start_at, timeZone)}
        </button>
      ))}
    </div>
  );
}

function TimeChooser({
  botId, timeZone, providers, services, initial, showService, allowAnyProvider = false, onSubmit, submitLabel, children,
}) {
  const [serviceId, setServiceId] = useState(initial.service_id || "");
  const [providerId, setProviderId] = useState(initial.provider_id ? String(initial.provider_id) : (allowAnyProvider ? ANY_PROVIDER : ""));
  const [date, setDate] = useState(initial.date);
  const [slot, setSlot] = useState("");
  const [custom, setCustom] = useState(false);
  const [customTime, setCustomTime] = useState(initial.time || "10:00");
  const [timeError, setTimeError] = useState("");

  const startAt = custom ? (date && customTime ? `${date}T${customTime}:00` : "") : slot;
  const activeProviders = providers.filter((p) => p.is_active || String(p.id) === String(initial.provider_id));

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        const now = new Date();
        const localNow = localParts(now.toISOString(), timeZone);
        const isPast = custom
          ? `${date}T${customTime}` <= `${localNow.date}T${localNow.time}`
          : new Date(startAt).getTime() <= now.getTime();
        if (isPast) {
          setTimeError("Appointment start time must be in the future.");
          return;
        }
        setTimeError("");
        onSubmit({
          service_id: serviceId ? Number(serviceId) : null,
          provider_id: providerId && providerId !== ANY_PROVIDER ? Number(providerId) : null,
          start_at: startAt,
          override_schedule: custom,
        });
      }}
    >
      {showService && services.length > 0 && (
        <Field label="Service" hint="Optional. Sets the appointment length.">
          <select className={inputClass} value={serviceId} onChange={(e) => { setServiceId(e.target.value); setSlot(""); }}>
            <option value="">No specific service</option>
            {services.filter((s) => s.is_active).map((s) => (
              <option key={s.id} value={s.id}>{s.name} ({s.duration_minutes} min)</option>
            ))}
          </select>
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label={allowAnyProvider ? "Provider (optional)" : "Provider"}>
          <select required={!allowAnyProvider} className={inputClass} value={providerId} onChange={(e) => { setProviderId(e.target.value); setSlot(""); }}>
            {allowAnyProvider && <option value={ANY_PROVIDER}>Any available provider</option>}
            {!allowAnyProvider && <option value="">Select…</option>}
            {activeProviders.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Date">
          <input required type="date" min={todayIn(timeZone)} className={inputClass} value={date} onChange={(e) => { setDate(e.target.value); setSlot(""); }} />
        </Field>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-600">Time</span>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={custom} onChange={(e) => setCustom(e.target.checked)} />
            Custom time (outside working hours)
          </label>
        </div>
        {custom ? (
          <input type="time" required className={inputClass} value={customTime} onChange={(e) => setCustomTime(e.target.value)} />
        ) : (
          <SlotPicker botId={botId} timeZone={timeZone} serviceId={serviceId} providerId={providerId} date={date} value={slot} onChange={setSlot} />
        )}
      </div>
      {children}
      <Alert>{timeError}</Alert>
      <div className="flex justify-end">
        <button type="submit" className={btnPrimary} disabled={(!allowAnyProvider && !providerId) || !startAt}>{submitLabel}</button>
      </div>
    </form>
  );
}

export function NewAppointmentModal({ open, onClose, botId, timeZone, providers, services, defaultDate, onCreated, customer }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [matches, setMatches] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setPhone(customer?.phone || ""); setName(customer?.name || ""); setNotes(""); setMatches([]); setCustomerId(customer?.id || null); setError("");
  }, [open, customer]);

  useEffect(() => {
    if (customer || phone.trim().length < 6) {
      setMatches([]);
      return;
    }
    const t = setTimeout(() => {
      searchCustomers(botId, { phone: phone.trim() }).then((d) => setMatches(d || [])).catch(() => setMatches([]));
    }, 300);
    return () => clearTimeout(t);
  }, [botId, phone, customer]);

  const submit = async (timeValues) => {
    setError("");
    try {
      const created = await createAppointment(botId, {
        ...timeValues,
        source: "admin",
        notes: notes.trim() || null,
        ...(customerId ? { customer_id: customerId } : { customer: { phone: phone.trim() || null, name: name.trim() || null } }),
      });
      onCreated(created);
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not create the appointment."));
    }
  };

  return (
    <Modal open={open} title="New appointment" onClose={onClose} wide>
      <Alert>{error}</Alert>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Phone (with country code)">
          <input className={inputClass} value={phone} readOnly={Boolean(customer)} onChange={(e) => { setPhone(e.target.value); setCustomerId(null); }} placeholder="9198xxxxxxxx" />
        </Field>
        <Field label="Name">
          <input className={inputClass} value={name} readOnly={Boolean(customer)} onChange={(e) => { setName(e.target.value); setCustomerId(null); }} required={!customerId} />
        </Field>
      </div>
      {matches.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-500">Existing:</span>
          {matches.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { setCustomerId(c.id); setName(c.name || ""); }}
              className={`rounded-lg border px-2 py-1 ${customerId === c.id ? "border-[#096dea] bg-blue-50 text-[#096dea]" : "border-slate-200 text-slate-700"}`}
            >
              {c.name || "Unnamed"}
            </button>
          ))}
        </div>
      )}
      <TimeChooser
        botId={botId}
        timeZone={timeZone}
        providers={providers}
        services={services}
        showService
        allowAnyProvider
        initial={{ date: defaultDate }}
        submitLabel="Book appointment"
        onSubmit={submit}
      >
        <Field label="Notes">
          <textarea rows={2} className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </TimeChooser>
    </Modal>
  );
}

function RescheduleModal({ appointment, onClose, botId, timeZone, providers, services, onSaved }) {
  const [error, setError] = useState("");
  useEffect(() => setError(""), [appointment]);
  if (!appointment) return null;
  const local = localParts(appointment.start_at, timeZone);

  const submit = async ({ provider_id, start_at }) => {
    setError("");
    try {
      await updateAppointment(botId, appointment.id, { provider_id, start_at });
      onSaved();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not reschedule."));
    }
  };

  return (
    <Modal open title={`Reschedule · ${appointment.customer_name || "Customer"}`} onClose={onClose} wide>
      <Alert>{error}</Alert>
      <TimeChooser
        botId={botId}
        timeZone={timeZone}
        providers={providers}
        services={services}
        initial={{ provider_id: appointment.provider_id, service_id: appointment.service_id, date: local.date, time: local.time }}
        submitLabel="Save new time"
        onSubmit={submit}
      />
    </Modal>
  );
}

function CancelModal({ appointment, onClose, botId, onSaved }) {
  const [by, setBy] = useState("admin");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setBy("admin"); setReason(""); setError(""); }, [appointment]);
  if (!appointment) return null;

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      await cancelAppointment(botId, appointment.id, { cancelled_by: by, cancel_reason: reason.trim() || null });
      onSaved();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not cancel."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title="Cancel appointment"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={btnSecondary} onClick={onClose}>Keep</button>
          <button type="button" className={btnDanger} onClick={submit} disabled={saving}>{saving ? "Cancelling…" : "Cancel appointment"}</button>
        </>
      }
    >
      <Alert>{error}</Alert>
      <Field label="Cancelled by">
        <select className={inputClass} value={by} onChange={(e) => setBy(e.target.value)}>
          <option value="admin">Staff</option>
          <option value="provider">Provider</option>
          <option value="customer">Customer</option>
        </select>
      </Field>
      <Field label="Reason">
        <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Doctor on emergency leave" />
      </Field>
    </Modal>
  );
}

export function AppointmentActions({ appointment, botId, timeZone, providers, services, onSaved }) {
  const { can } = useAuth();
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const act = async (action) => {
    setBusy(true);
    setError("");
    try {
      await action(botId, appointment.id);
      await onSaved();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not update appointment."));
    } finally {
      setBusy(false);
    }
  };
  const afterModal = () => {
    setModal(null);
    return onSaved();
  };

  const canUpdate = can("appointments.update");
  const canCancel = can("appointments.cancel");
  const canComplete = can("appointments.complete");
  if ((!canUpdate && !canCancel && !canComplete) || appointment.status !== "confirmed") return null;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {canComplete && <><button type="button" className={btnSecondary} disabled={busy} onClick={() => act(completeAppointment)}>Done</button>
          <button type="button" className={btnSecondary} disabled={busy} onClick={() => act(markNoShow)}>No-show</button></>}
        {canUpdate && <button type="button" className={btnSecondary} disabled={busy} onClick={() => setModal("reschedule")}>Reschedule</button>}
        {canCancel && <button type="button" className={btnDanger} disabled={busy} onClick={() => setModal("cancel")}>Cancel</button>}
      </div>
      <Alert>{error}</Alert>
      {modal === "reschedule" && <RescheduleModal appointment={appointment} onClose={() => setModal(null)} botId={botId} timeZone={timeZone} providers={providers} services={services} onSaved={afterModal} />}
      {modal === "cancel" && <CancelModal appointment={appointment} onClose={() => setModal(null)} botId={botId} onSaved={afterModal} />}
    </div>
  );
}

export default function AppointmentsPanel({ botId, timeZone, providers, services, appointmentView = false, onCustomer, refreshKey }) {
  const { can } = useAuth();
  const [date, setDate] = useState(() => todayIn(timeZone));
  const [period, setPeriod] = useState("today");
  const [endDate, setEndDate] = useState(() => todayIn(timeZone));
  const requestRef = useRef(0);
  const rangeStart = appointmentView && period !== "range" ? todayIn(timeZone) : date;
  const rangeEnd = period === "today" ? rangeStart : period === "week" ? addDays(rangeStart, 6) : endDate;
  const days = !appointmentView || period === "today" ? 1 : period === "week" ? 7
    : Math.round((Date.parse(endDate) - Date.parse(date)) / 86400000) + 1;
  const rangeError = !rangeStart || !Number.isFinite(days) || days < 1
    ? "Choose an end date on or after the start date."
    : days > 62 ? "Date range cannot exceed 62 days." : "";
  const [providerId, setProviderId] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [notice, setNotice] = useState("");
  const [viewMode, setViewMode] = useState("card");

  const providerName = (id) => providers.find((p) => p.id === id)?.name || `#${id}`;
  const serviceName = (id) => services.find((s) => s.id === id)?.name;

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    if (rangeError) {
      setRows([]);
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await listAppointments(botId, {
        date: rangeStart,
        ...(appointmentView ? { days } : {}),
        provider_id: providerId || undefined,
        status: status || undefined,
      });
      if (requestId === requestRef.current) setRows([...(data || [])].sort((a, b) => new Date(a.start_at) - new Date(b.start_at)));
    } catch (err) {
      if (requestId === requestRef.current) setError(getSchedulingErrorMessage(err, "Could not load appointments."));
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [botId, rangeStart, days, appointmentView, rangeError, providerId, status]);

  useEffect(() => {
    setRows([]);
    load();
    return () => { requestRef.current += 1; };
  }, [load, refreshKey]);

  useBotNotifications(botId, { topic: "appointment", onRefresh: load });

  const act = async (fn) => {
    setError("");
    try {
      await fn();
      await load();
    } catch (err) {
      setError(getSchedulingErrorMessage(err));
    }
  };

  const afterModal = (created) => {
    setShowNew(false);
    setNotice(created?.whatsapp_notification ? `Appointment booked. ${notificationOutcomeText(created.whatsapp_notification, "confirmation")}` : "Appointment booked.");
    load();
  };

  const customerInfo = (appointment) => (
    <div className="flex items-center gap-3">
      <CustomerAvatar botId={botId} customer={{
        id: appointment.customer_id, name: appointment.customer_name || "Unnamed",
        profile_picture_file_id: appointment.customer_profile_picture_file_id,
        profile_picture_url: appointment.customer_profile_picture_url,
      }} />
      <div className="min-w-0">
        {onCustomer && appointment.customer_id
          ? <button type="button" className="text-left font-semibold text-cyan-800 underline" onClick={() => onCustomer({ id: appointment.customer_id })}>{appointment.customer_name || "Unnamed"}</button>
          : <p className="font-semibold text-slate-900">{appointment.customer_name || "Unnamed"}</p>}
        {appointment.customer_phone && <p className="text-xs text-slate-500">{appointment.customer_phone}</p>}
      </div>
    </div>
  );
  const appointmentDetails = (appointment) => (
    <div className="space-y-1 text-sm">
      <p className="font-semibold text-slate-900">{formatDate(localParts(appointment.start_at, timeZone).date)}</p>
      <p className="text-slate-600">{formatTime(appointment.start_at, timeZone)} – {formatTime(appointment.end_at, timeZone)}</p>
      <p className="text-xs text-slate-500">{providerName(appointment.provider_id)}{serviceName(appointment.service_id) && ` · ${serviceName(appointment.service_id)}`} · via {appointment.source}</p>
      {appointment.notes && <p className="whitespace-pre-wrap text-xs text-slate-600">{appointment.notes}</p>}
      {appointment.status === "cancelled" && appointment.cancel_reason && <p className="text-xs text-rose-600">Cancelled by {appointment.cancelled_by}: {appointment.cancel_reason}</p>}
      <NotificationStatus botId={botId} resourceType="appointment" resourceId={appointment.id} initial={appointment.whatsapp_notification || null} compact />
    </div>
  );
  const appointmentActions = (appointment) => appointment.status === "confirmed"
    ? <AppointmentActions appointment={appointment} botId={botId} timeZone={timeZone} providers={providers} services={services} onSaved={load} />
    : can("appointments.update") ? <button type="button" className={btnSecondary} onClick={() => act(() => updateAppointment(botId, appointment.id, { status: "confirmed" }))}>Restore</button> : null;

  return (
    <div className={`${card} space-y-4`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          {appointmentView ? <>
            <div role="group" aria-label="Appointment period" className="flex flex-wrap gap-2">
              {[["today", "Today"], ["week", "This week"], ["range", "Date range"]].map(([key, label]) =>
                <button key={key} type="button" className={period === key ? btnPrimary : btnSecondary} aria-pressed={period === key} onClick={() => {
                  if (key === "range") {
                    setDate(rangeStart);
                    setEndDate(rangeEnd);
                  }
                  setPeriod(key);
                }}>{label}</button>)}
            </div>
            <DateRangePicker start={rangeStart} end={rangeEnd} onChange={(start, end) => {
              setDate(start);
              setEndDate(end);
              setPeriod("range");
            }} />
          </> : <>
          <button type="button" className={btnSecondary} onClick={() => setDate((d) => addDays(d, -1))} aria-label="Previous day"><ChevronLeft size={16} /></button>
          <input type="date" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Date" />
          <button type="button" className={btnSecondary} onClick={() => setDate((d) => addDays(d, 1))} aria-label="Next day"><ChevronRight size={16} /></button>
          <button type="button" className={btnSecondary} onClick={() => setDate(todayIn(timeZone))}>Today</button>
          </>}
          <select className="rounded-xl border border-slate-200 px-3 py-2 text-sm" value={providerId} onChange={(e) => setProviderId(e.target.value)} aria-label={appointmentView ? "Doctor filter" : "Provider filter"}>
            <option value="">{appointmentView ? "All doctors" : "All providers"}</option>
            {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select className="rounded-xl border border-slate-200 px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status filter">
            <option value="">All statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="no_show">No-show</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button type="button" className={btnSecondary} onClick={load} aria-label="Refresh"><RefreshCw size={16} /></button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {appointmentView && <div role="group" aria-label="Appointment view" className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
            {[["card", "Card view", LayoutGrid], ["table", "Table view", Table2]].map(([value, label, Icon]) => (
              <button key={value} type="button" aria-label={label} title={label} aria-pressed={viewMode === value} onClick={() => setViewMode(value)}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${viewMode === value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}><Icon size={16} /></button>
            ))}
          </div>}
          {can("appointments.create") && <button type="button" className={btnPrimary} onClick={() => setShowNew(true)} disabled={!providers.some((p) => p.is_active)}>
          <Plus size={16} /> New appointment
          </button>}
        </div>
      </div>

      {!rangeError && <p className="text-xs text-slate-500">{formatDate(rangeStart)}{days > 1 ? ` – ${formatDate(addDays(rangeStart, days - 1))}` : ""} · times shown in {timeZone}{appointmentView && period === "week" ? " · Next 7 days, starting today" : ""}</p>}
      <Alert>{rangeError}</Alert>
      <Alert>{error}</Alert>
      {rows.length >= 500 && <Alert kind="info">The result limit of 500 appointments was reached. Narrow the date range or doctor filter to see all matching appointments.</Alert>}
      <Alert kind="info">{notice}</Alert>
      {!providers.length && <Alert kind="info">Add a provider and working hours first (Providers tab).</Alert>}

      {rangeError ? null : loading && rows.length === 0 ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10 text-slate-400">
          <CalendarClock size={28} />
          <p className="text-sm">{appointmentView ? "No appointments match these filters." : "No appointments on this day."}</p>
        </div>
      ) : appointmentView ? (
        viewMode === "card" ? (
          <ul aria-label="Appointment cards" className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {rows.map((appointment) => (
              <li key={appointment.id} className="flex min-w-0 flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  {customerInfo(appointment)}
                  <StatusPill status={appointment.status} />
                </div>
                {appointmentDetails(appointment)}
                <div className="mt-auto border-t border-slate-100 pt-3">{appointmentActions(appointment)}</div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="overflow-x-auto">
            <table aria-label="Appointments" className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                <tr>{["Customer", "Appointment", "Status", "Actions"].map((heading) => <th key={heading} scope="col" className="px-4 py-3">{heading}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((appointment) => (
                  <tr key={appointment.id}>
                    <td className="px-4 py-4 align-top">{customerInfo(appointment)}</td>
                    <td className="min-w-56 px-4 py-4 align-top">{appointmentDetails(appointment)}</td>
                    <td className="px-4 py-4 align-top"><StatusPill status={appointment.status} /></td>
                    <td className="min-w-64 px-4 py-4 align-top">{appointmentActions(appointment)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((a) => (
            <li key={a.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
              <div className="flex gap-4">
                <div className="w-24 shrink-0 text-sm font-bold text-slate-900">
                  {appointmentView && <div className="text-xs font-normal">{formatDate(localParts(a.start_at, timeZone).date)}</div>}
                  {formatTime(a.start_at, timeZone)}
                  <div className="text-xs font-normal text-slate-400">to {formatTime(a.end_at, timeZone)}</div>
                </div>
                <div className="space-y-0.5 text-sm">
                  <div className="font-semibold text-slate-900">
                    {onCustomer && a.customer_id ? <button type="button" className="text-cyan-800 underline" onClick={() => onCustomer({ id: a.customer_id })}>{a.customer_name || "Unnamed"}</button> : a.customer_name || "Unnamed"} {a.customer_phone && <span className="font-normal text-slate-500">· {a.customer_phone}</span>}
                  </div>
                  <div className="text-xs text-slate-600">
                    {providerName(a.provider_id)}
                    {serviceName(a.service_id) && ` · ${serviceName(a.service_id)}`} · via {a.source}
                  </div>
                  {a.notes && <div className="text-xs text-slate-500">“{a.notes}”</div>}
                  <NotificationStatus botId={botId} resourceType="appointment" resourceId={a.id} initial={a.whatsapp_notification || null} compact />
                  {a.status === "cancelled" && a.cancel_reason && (
                    <div className="text-xs text-rose-600">Cancelled by {a.cancelled_by}: {a.cancel_reason}</div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill status={a.status} />
                {a.status === "confirmed" ? (
                  <AppointmentActions appointment={a} botId={botId} timeZone={timeZone} providers={providers} services={services} onSaved={load} />
                ) : (
                  <button type="button" className={btnSecondary} onClick={() => act(() => updateAppointment(botId, a.id, { status: "confirmed" }))}>
                    Restore
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <NewAppointmentModal
        open={showNew}
        onClose={() => setShowNew(false)}
        botId={botId}
        timeZone={timeZone}
        providers={providers}
        services={services}
        defaultDate={rangeStart}
        onCreated={afterModal}
      />
    </div>
  );
}
